import { forwardRef, useState, useEffect, useRef, InputHTMLAttributes } from 'react';

interface InputIntegerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  prefix?: string;
  suffix?: string;
  label?: string;
  error?: string;
}

export const InputInteger = forwardRef<HTMLInputElement, InputIntegerProps>(
  ({ value, onChange, prefix = '', suffix = '', label, error, className = '', ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const isFocusedRef = useRef(false);
    const lastValueRef = useRef<number | null | undefined>(undefined);

    const formatNumber = (num: number): string => {
      return num.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    // Atualiza o display apenas quando o valor externo muda E o input não está focado
    useEffect(() => {
      if (isFocusedRef.current) return;
      if (value === lastValueRef.current) return;
      lastValueRef.current = value as number | null | undefined;

      if (value === null || value === undefined || value === '') {
        setDisplayValue('');
      } else {
        const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
        if (!isNaN(numValue)) {
          setDisplayValue(formatNumber(numValue));
        }
      }
    }, [value]);

    const parseNumber = (str: string): number | null => {
      if (!str) return null;
      const cleaned = str.replace(/\./g, '');
      const num = parseInt(cleaned, 10);
      return isNaN(num) ? null : num;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (!inputValue) {
        setDisplayValue('');
        onChange(null);
        return;
      }

      // Permitir apenas dígitos
      const digits = inputValue.replace(/\D/g, '');

      if (!digits) {
        setDisplayValue('');
        onChange(null);
        return;
      }

      const numValue = parseInt(digits, 10);

      // Formatar com pontos de milhar enquanto digita
      const formatted = formatNumber(numValue);
      setDisplayValue(formatted);
      onChange(numValue);
    };

    const handleFocus = () => {
      isFocusedRef.current = true;
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
      const numValue = parseNumber(displayValue);

      if (numValue !== null) {
        setDisplayValue(formatNumber(numValue));
        lastValueRef.current = numValue;
      } else {
        setDisplayValue('');
        lastValueRef.current = null;
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`
              w-full px-3 py-2 border rounded-lg shadow-sm text-right
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${prefix ? 'pl-10' : ''}
              ${suffix ? 'pr-10' : ''}
              ${error ? 'border-red-500' : 'border-gray-300'}
              ${className}
            `}
            {...props}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              {suffix}
            </span>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

InputInteger.displayName = 'InputInteger';
